/*
 * Copyright (c) 2021. FINN Sp. z o.o.
 */

package pl.finn.forms.model

import io.kvision.core.Component
import io.kvision.core.Placement
import io.kvision.core.TooltipOptions
import io.kvision.core.enableTooltip
import io.kvision.form.FormControl
import io.kvision.form.FormType
import io.kvision.form.check.CheckBox
import io.kvision.form.check.RadioGroup
import io.kvision.form.check.checkBox
import io.kvision.form.text.Text
import io.kvision.form.text.TextInput
import io.kvision.form.text.text
import io.kvision.form.text.textInput
import io.kvision.form.upload.Upload
import io.kvision.form.upload.UploadInput
import io.kvision.form.upload.upload
import io.kvision.form.upload.uploadInput
import io.kvision.html.*
import io.kvision.modal.Alert
import io.kvision.pace.Pace
import pl.finn.forms.POLE_WYMAGANE
import pl.finn.forms.generator.DocumentFormModel
import pl.finn.forms.generator.ElementMatcher
import pl.finn.forms.utils.ATTACHMENTS
import pl.finn.forms.utils.FILE_EXTENSIONS
import pl.finn.forms.utils.form.FormBuilder
import pl.finn.forms.utils.form.FormElement
import pl.finn.forms.utils.form.extendedPanel
import pl.finn.forms.utils.getRandomString

class ComponentsGroup(elementMatcher: ElementMatcher, reference: FormBuilder<DocumentTreeNode>) {

  private val formReference = reference
  private var formAttachment: FormBuilder<DocumentTreeNode>? = null
  private val matcher = elementMatcher

  private val components = Components(matcher, formReference)

  suspend fun createElementsGroup(element: DocumentTreeNode, parentElements: DocumentTreeNode?): Div {
    val labelContent = getLabel(element)
    val elementId = "finn_" + getRandomString()
    val matchedElement = components.createChoiceElement(element, null, parentElements)
    val isChecked = !element.value.equals("false") && !element.value.isNullOrEmpty()
    val collapseStatus = if (isChecked) "show" else ""
    val collapsedDiv = Div(className = "collapse $collapseStatus") { id = elementId }
    return (
        Div {
          checkBox(label = labelContent!!, rich = true) {
            this.value = isChecked
            addCssClass("mt-3")
            addCssClass("w-fit")
            setAttribute("data-bs-toggle", "collapse")
            setAttribute("data-bs-target", "#$elementId")
            setAttribute("aria-expanded", isChecked.toString())
            setAttribute("aria-controls", elementId)
            onClick {
              formReference.getData()
              val control = formReference.getControlField(element)
              val status = this.value
              control?.visible = status
              changeElementChildrenStatus(element, status)
              if (status) collapsedDiv.addCssClass("show") else collapsedDiv.removeCssClass("show")
            }
          }
          collapsedDiv.add(div(className = "pb-2") {
            div(className = "pb-2") {
              add(matchedElement)
              val control = formReference.getControlField(element)
              control?.visible = isChecked
              changeElementChildrenStatus(element, isChecked)
            }
          })
          add(collapsedDiv)
        }
        )
  }

  fun createCollapseCheckbox(element: DocumentTreeNode, nestedElements: LinkedHashMap<DocumentTreeNode, Any>): Div {
    val labelContent = getLabel(element)
    val elementId = "finn_" + getRandomString()
    val isChecked = element.value.equals("true")
    val collapseStatus = if (isChecked) "show" else ""
    val shouldBeExpanded = (isChecked || element.metaModel?.isElementRequired == true)
    val collapsedDiv = Div(className = "collapse $collapseStatus") { id = elementId }
    val formElement = FormElement(
      element,
      CheckBox(label = labelContent!!, rich = true) {
        this.value = isChecked
        addCssClass("mt-3")
        addCssClass("w-fit")
        setAttribute("data-bs-toggle", "collapse")
        setAttribute("data-bs-target", "#$elementId")
        setAttribute("aria-expanded", shouldBeExpanded.toString())
        setAttribute("aria-controls", elementId)
        onClick {
          formReference.getData()
          val status = this.value
          changeElementChildrenStatus(element, status)
          if (status) collapsedDiv.addCssClass("show") else collapsedDiv.removeCssClass("show")
        }
      },
      required = element.metaModel?.isElementRequired == true,
      requiredMessage = POLE_WYMAGANE,
    )

    formReference.getElement(formElement)
    return (
        Div {
          add(formElement.field)
          collapsedDiv.add(div(className = "pb-2") {
            for (nested in nestedElements) {
              add(nested.value as Component)
            }
            changeElementChildrenStatus(element, isChecked)
          })
          add(collapsedDiv)
        }
        )
  }

  private fun changeElementChildrenStatus(element: DocumentTreeNode, status: Boolean) {
    val controlParent = formReference.getControlField(element)
    if (controlParent !is RadioGroup || !status) {
      if (element.children.isNotEmpty()) {
        element.children.forEach { it1 ->
          val control = formReference.getControlField(it1)
          if (it1 != null && (control !is RadioGroup || !status)) {
            changeElementChildrenStatus(it1, status)
          }
          val nestedControl = formReference.getControlField(it1)
          nestedControl?.visible = status
        }
      }
    }
  }

  fun createDivGroupElement(
    element: DocumentTreeNode,
    nestedElements: LinkedHashMap<DocumentTreeNode, Any>,
    parentElements: DocumentTreeNode?
  ): Div? {
    val div = Div(rich = true)
    if (getLabel(element).isNullOrEmpty() && element.metaModel?.isElementRequired == false) {
      return null
    }
    nestedElements.forEach {
      div.add(it.value as Component)
    }
    if (element.metaModel?.footer != null) {
      div.add(element.metaModel?.footer?.let { components.createFooter(it) }!!)
    }
    if (parentElements?.children != null && element.metaModel?.particleView?.get(0)?.addButtonName != null) {
      components.shouldRenderButtonDiv(element)
      div.add(components.createButtonGroup(element, parentElements.children))
    }
    return div
  }

  fun addAttachments(element: DocumentTreeNode, formReference: FormBuilder<DocumentTreeNode>): Div {
    val labelContent = if (!element.metaModel?.label.isNullOrEmpty()) element.metaModel?.label else element.label
    val name = if (!element.name.isNullOrEmpty()) element.name else ""
    var attachment = Upload()
    var attachmentDescription = Text()
    return (
        Div(className = "form-group mb-3") {
          label(labelContent ?: ATTACHMENTS, rich = true)
          extendedPanel<DocumentTreeNode>(type = FormType.INLINE) {
            formAttachment = this
            div(className = "row") {
              div(className = "col-lg-5 py-2") {
                val findName = findElementByName(element, "Nazwa")
                attachment = if (findName != null) createUploadInput(findName) as Upload
                else {
                  upload("/") {
                    placeholder = "Wybierz plik..."
                    explorerTheme = true
                    showUpload = false
                    showCancel = false
                    showPreview = false
                    dropZoneEnabled = false
                    allowedFileExtensions = FILE_EXTENSIONS
                  }
                }
                add(attachment.input)
              }
              div(className = "col-lg-7") {
                div(className = "row") {
                  div(className = "col-sm-8 py-2") {
                    val findName = findElementByName(element, "Opis")
                    attachmentDescription = if (findName != null) createFileDescriptionInput(findName) as Text else
                      text(value = element.label ?: "") {
                        placeholder = "Wprowadź opis załącznika"
                        addCssClass("form-control")
                      }
                    add(attachmentDescription.input)
                  }
                  div(className = "col-sm-4 py-2") {
                    button("Dodaj", "fas fa-upload", className = "w-100").onClick {
                      formReference.getData()
                      if (formAttachment?.validate() == false) {
                        Alert.show(
                          "Sprawdzenie załączników",
                          "Nie można dodać załącznika<br/>" +
                              "Sprawdź czy nazwa załącznika zawiera znaki inne niż litery, cyfry lub - _ . " +
                              "oraz czy został wypełniony opis załącznika",
                          rich = true
                        )
                      } else {
                        Pace.show()
                        DocumentFormModel.addAttachment(
                          attachment,
                          attachmentDescription.value ?: name?.removeRange(0, name.lastIndexOf('.')),
                          name
                        )
                        Pace.hide()
                      }
                    }
                  }
                }
              }
            }
            if (!element.metaModel?.help.isNullOrEmpty()) {
              enableTooltip(TooltipOptions(element.metaModel?.help, rich = true, placement = Placement.BOTTOM))
            }
          }
        })
  }

  private fun createUploadInput(element: DocumentTreeNode): FormControl {
    val formElement = FormElement(
      element,
      Upload("/") {
        placeholder = "Wybierz plik..."
        explorerTheme = true
        showUpload = false
        showCancel = false
        showPreview = false
        dropZoneEnabled = false
        allowedFileExtensions = FILE_EXTENSIONS
      },
      required = element.metaModel?.isElementRequired == true,
      requiredMessage = POLE_WYMAGANE,
      validatorMessage = { "Nazwa pliku zawiera niedozwolone znaki" }) {
      it.getValueAsString()?.let { it1 -> ("[a-zA-Z0-9_\\.\\-]{5,55}")?.toRegex()?.matches(it1) }}

    formAttachment?.getElement(formElement)
    return (formElement.field)
  }

  private fun createFileDescriptionInput(element: DocumentTreeNode): FormControl {
    val formElement = FormElement(
      element,
      Text {
        placeholder = "Wprowadź opis załącznika"
      },
      required = element.metaModel?.isElementRequired == true,
      requiredMessage = POLE_WYMAGANE,
    )

    formAttachment?.getElement(formElement)
    return (formElement.field)
  }

  private fun findElementByName(element: DocumentTreeNode, findIt: String): DocumentTreeNode? {
    element.children.forEach {
      if (it?.name?.contains(findIt) == true) return it
    }
    return null
  }

  private fun getLabel(element: DocumentTreeNode) =
    if (!element.metaModel?.label.isNullOrEmpty()) element.metaModel?.label
    else if (!element.label.isNullOrEmpty()) element.label
    else ""

  fun createHeader(element: DocumentTreeNode): Component {
    return (
        H1(
          element.label,
          className = "h4 text-center mb-4"
        ) {
          tabindex = -1
        }
        )
  }
}
